import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SearchFilterPanel from '../../components/ui/SearchFilterPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import JourneyCard from './components/JourneyCard';
import AnalyticsPanel from './components/AnalyticsPanel';
import ClassroomSummary from './components/ClassroomSummary';
import PendingQuestions from './components/PendingQuestions';
import SystemNotifications from './components/SystemNotifications';
import { useAuth } from '../../contexts/AuthContext';
import { getTeacherTrails } from '../../services/learningTrailService';
import { getTeacherAnalytics } from '../../services/teacherAnalyticsService';
import { getClassrooms } from '../../services/classroomService';
import { supabase } from '../../lib/supabase';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});

  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalJourneys: 0,
    activeStudents: 0,
    avgCompletionRate: 0,
    pendingQuestions: 0,
    recentActivity: [],
    topJourneys: []
  });
  const [classrooms, setClassrooms] = useState([]);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Fetch classrooms
  useEffect(() => {
    const fetchClassrooms = async () => {
      if (!user?.id) return;
      try {
        const data = await getClassrooms();
        setClassrooms(data || []);
      } catch (err) {
        console.error('Failed to fetch classrooms:', err);
      }
    };
    fetchClassrooms();
  }, [user?.id]);

  // Fetch learning trails on component mount
  useEffect(() => {
    const fetchTrails = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const { trails, error: fetchError } = await getTeacherTrails(user?.id);

      if (fetchError) {
        console.error('Failed to fetch trails:', fetchError);
        setError(fetchError);
        setLoading(false);
        return;
      }

      // Fetch student_progress counts for all trails
      const trailIds = trails?.map(t => t?.id) || [];
      let progressByTrail = {};

      if (trailIds?.length > 0) {
        const { data: progressData, error: progressError } = await supabase?.from('student_progress')?.select('trail_id, completed_at')?.in('trail_id', trailIds);

        if (!progressError && progressData) {
          progressData?.forEach(p => {
            if (!progressByTrail?.[p?.trail_id]) {
              progressByTrail[p.trail_id] = { attempts: 0, completions: 0 };
            }
            progressByTrail[p.trail_id].attempts += 1;
            if (p?.completed_at) {
              progressByTrail[p.trail_id].completions += 1;
            }
          });
        }
      }

      // Transform trails data to match JourneyCard format
      const transformedJourneys = trails?.map(trail => {
        const trailClassrooms = trail?.trail_classrooms || [];
        const classroomNames = trailClassrooms
          ?.map(tc => tc?.classrooms?.classroom_name)
          ?.filter(Boolean);
        const classroomDisplay = classroomNames?.length > 0
          ? classroomNames?.join(', ')
          : 'No classroom';
        const gradeLevel = trailClassrooms?.[0]?.classrooms?.grade_level;

        const trailStats = progressByTrail?.[trail?.id] || { attempts: 0, completions: 0 };
        const engagementLevel = trailStats?.attempts > 0
          ? Math.round((trailStats?.completions / trailStats?.attempts) * 100)
          : 0;

        return {
          id: trail?.id,
          title: trail?.title,
          description: trail?.description,
          thumbnail: trail?.image_url || '/assets/images/no_image.png',
          thumbnailAlt: `${trail?.title} learning trail thumbnail`,
          status: trail?.published ? 'published' : 'draft',
          classroom: classroomDisplay,
          createdDate: new Date(trail?.created_at)?.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          }),
          subject: gradeLevel ? `Grade ${gradeLevel}` : null,
          minGradeLevel: trail?.min_grade_level && trail?.min_grade_level !== 'All Grades' ? trail?.min_grade_level : null,
          attempts: trailStats?.attempts,
          completions: trailStats?.completions,
          engagementLevel
        };
      }) || [];

      setJourneys(transformedJourneys);
      setLoading(false);
    };

    fetchTrails();
  }, [user?.id]);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user?.id) return;

      const { analytics: analyticsData, error: analyticsError } = await getTeacherAnalytics(user?.id);

      if (analyticsError) {
        console.error('Failed to fetch analytics:', analyticsError);
        return;
      }

      if (analyticsData) {
        setAnalytics(analyticsData);
      }
    };

    fetchAnalytics();
  }, [user?.id]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    console.log('Search query:', query);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters changed:', newFilters);
  };

  const handleViewJourney = (journeyId) => {
    console.log('View journey:', journeyId);
  };

  const handleEditJourney = (journeyId) => {
    navigate('/learning-trails-creator', { state: { journeyId, mode: 'edit' } });
  };

  const handleDeleteJourney = (journeyId) => {
    console.log('Delete journey:', journeyId);
  };

  const handleCreateNew = () => {
    navigate('/learning-trails-creator');
  };

  const handleManageClassrooms = () => {
    navigate('/classroom-management');
  };

  const handleViewAllQuestions = () => {
    console.log('View all questions');
  };

  const handleDismissNotification = (notificationId) => {
    setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 md:mb-8 space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                Dashboard
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                Welcome back! Manage your learning trails and track student progress.
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="PlusCircle"
              onClick={handleCreateNew}>
              
              Create New Journey
            </Button>
          </div>

          <div className="mb-6 md:mb-8">
            <AnalyticsPanel
              analytics={analytics}
              isExpanded={analyticsExpanded}
              onToggle={() => setAnalyticsExpanded(!analyticsExpanded)} />
            
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-2 space-y-6">
              <SearchFilterPanel
                onSearch={handleSearch}
                onFilterChange={handleFilterChange}
                searchPlaceholder="Search learning trails..."
                showAdvancedFilters={true} />
              

              <div className="bg-card rounded-lg shadow-sm p-4 md:p-5 lg:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                    Learning Trails
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {journeys?.length} {journeys?.length === 1 ? 'trail' : 'trails'}
                  </span>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                      <Icon name="Loader" size={40} className="text-muted-foreground animate-spin" />
                    </div>
                    <p className="text-sm text-muted-foreground">Loading learning trails...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="AlertCircle" size={40} className="text-error" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Failed to load trails</h3>
                    <p className="text-sm text-muted-foreground mb-6">{error}</p>
                    <Button variant="outline" onClick={() => window.location?.reload()}>
                      Retry
                    </Button>
                  </div>
                ) : journeys?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
                    {journeys?.map((journey) => (
                      <JourneyCard
                        key={journey?.id}
                        journey={journey}
                        onView={handleViewJourney}
                        onEdit={handleEditJourney}
                        onDelete={handleDeleteJourney} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Search" size={40} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">No journeys found</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Try adjusting your search or filters
                    </p>
                    <Button variant="outline" onClick={() => {setSearchQuery('');setFilters({});}}>
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <ClassroomSummary
                classrooms={classrooms}
                onManageClick={handleManageClassrooms} />
              

              <PendingQuestions
                questions={pendingQuestions}
                onViewAll={handleViewAllQuestions} />
              

              <SystemNotifications
                notifications={notifications}
                onDismiss={handleDismissNotification} />
              
            </div>
          </div>
        </div>
      </main>
    </div>
  );

};

export default TeacherDashboard;